import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Box,
  styled,
} from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";

import { APIError } from "../../components/APIError";
import { CustomPaginatedTableContainer } from "../../components/CustomTableComponents";
import { ColumnHead } from "../../components/CustomTableComponents/ColumnHead";
import { LoadingRow } from "../../components/CustomTableComponents/LoadingRow";
import { PageHeader } from "../../components/PageHeader";
import { ActivityViewFilters } from "./components/ActivityViewFilters";
import { FilterType, FILTER_TYPES } from "../../api/services";
import { useAccountActivities } from "../../api/hooks/useAccountActivities";
import { ActivityTableBody } from "./components/ActivityTableBody";

const ActivityPageHeader = styled(PageHeader)(({ theme }) => ({
  minWidth: theme.spacing(25),
  marginBottom: theme.spacing(1),
}));

const EmptyRow: React.FC = () => {
  const { pageNumber, pageFilter } = useParams();
  const emptyMessage = useMemo(
    () =>
      pageFilter === undefined && pageNumber === 1
        ? "No activities for this account."
        : pageNumber > 1
        ? "No more activities for this account."
        : "No activities found with the current filter.",
    [pageFilter, pageNumber],
  );
  return (
    <TableRow>
      <TableCell colSpan={4} data-testid="ActivityView__Empty">
        {emptyMessage}
      </TableCell>
    </TableRow>
  );
};

const ActivityTableHead: React.FC = () => (
  <TableHead data-testid="ActivityView__TableHead">
    <TableRow>
      <ColumnHead>Summary</ColumnHead>
      <ColumnHead boxProps={{ width: "180px" }}>Type</ColumnHead>
      <ColumnHead boxProps={{ width: "200px" }}>User</ColumnHead>
      <ColumnHead boxProps={{ width: "200px" }}>Date</ColumnHead>
    </TableRow>
  </TableHead>
);

export const ActivityView: React.FC = () => {
  const history = useHistory();

  const { accountNumber, pageNumber, pageFilter } = useParams();
  const [filter, setFilter] = useState<FilterType | undefined>(() =>
    FILTER_TYPES.includes(pageFilter) ? pageFilter : undefined,
  );
  const {
    page,
    pageSize,
    setPage,
    count,
    data: activities,
    status,
    error,
  } = useAccountActivities(
    accountNumber,
    pageNumber ? Number(pageNumber) : undefined,
    filter,
  );

  const updateUrl = useCallback(
    (p, f) => {
      const url =
        p === 1 && f == null
          ? `/accounts/${accountNumber}/activities`
          : f == null
          ? `/accounts/${accountNumber}/activities/${p}`
          : `/accounts/${accountNumber}/activities/${p}/${f}`;
      history.push(url);
    },
    [accountNumber, history],
  );

  useEffect(() => {
    // trigger query after url change
    const requestPage = pageNumber ? Number(pageNumber) : 1;
    if (requestPage !== page) {
      setPage(Number(pageNumber));
    }
    if (
      (FILTER_TYPES.includes(pageFilter) || pageFilter === undefined) &&
      pageFilter !== filter
    ) {
      setFilter(pageFilter);
    }
  }, [setPage, filter, page, pageFilter, pageNumber, setFilter]);

  return error ? (
    <APIError title="We could not load activities for this account." />
  ) : (
    <Box display="flex" flexDirection="column" data-testid="ActivityView">
      <Box display="flex" flexDirection="row" flexWrap="wrap" mb={2}>
        <ActivityPageHeader title="View">
          <ActivityViewFilters
            filter={filter}
            setFilter={(f) => updateUrl(1, f)}
          />
        </ActivityPageHeader>
      </Box>
      <CustomPaginatedTableContainer
        maxWidth="1280px"
        width="100%"
        paginationProps={{
          page,
          pageSize,
          count,
          setPage: (p) => updateUrl(p, filter),
        }}
      >
        <Table>
          <ActivityTableHead />
          <TableBody>
            {status === "loading" ? (
              <LoadingRow col={4} />
            ) : activities && activities.length > 0 ? (
              <ActivityTableBody activities={activities} />
            ) : (
              <EmptyRow />
            )}
          </TableBody>
        </Table>
      </CustomPaginatedTableContainer>
    </Box>
  );
};
